import Tone from 'tone';
import {insertEvent} from 'session/operations/actions.js';
import PatternSwitchEvent from 'session/PatternSwitchEvent.js';
import {setCurrentExportInfo} from 'actions/index.js';
import ethereum from 'decentralized/ethereum.js';
import {exportToWAV} from 'api/exportToWav.js';
import {MASTER, GROUPS} from 'constants/render-options.js';
import {
    setIsPlayingMix,
    setOfflineEngine
} from 'actions/index.js';
import ScriptableSequencer from './ScriptableSequencer.js';
import LFOTrack from 'track/LFOTrack.js';
import Scripting from 'scripting/Scripting.js';
import SoundUploader from 'api/SoundUploader.js';
import Sample from 'repos/Sample.js';

const SILENT = -80;
const SEND_NAMES = ['A', 'B', 'C', 'D'];

const BOUNCE_PATTERNS = 'BOUNCE_PATTERNS';
const BOUNCE_MIX = 'BOUNCE_MIX';
const BOUNCE_INSTRUMENT = 'BOUNCE_INSTRUMENT';

export const bounceTrackInstrument = (sequencerEngine, track) => {
    return new Promise(async (resolve, reject) => {
        let lenInBars = 48;
        resolve(await bouncePart({
            sequencerEngine,
            lenInBars,
            track,
            numPatterns: track.patterns.length,
            bounceType: BOUNCE_INSTRUMENT,
            download: false}));
        sequencerEngine.subSequences.forEach(
            sub => sub.mix.updateAllTypeRedux());
    });
};

export const bounceTrackPatterns = (sequencerEngine, track) => {
    return new Promise(async (resolve, reject) => {
        let lenInBars = 0;
        for (let patternNumber in track.patterns) {
            lenInBars +=  getPatternLength(sequencerEngine, parseInt(patternNumber), [track]) / 32;
        }
        lenInBars *= sequencerEngine.state.freeze.loops;
        resolve(await bouncePart({
            sequencerEngine,
            lenInBars,
            track,
            numPatterns: track.patterns.length,
            bounceType: BOUNCE_PATTERNS,
            download: false}));
        sequencerEngine.subSequences.forEach(
            sub => sub.mix.updateAllTypeRedux());
    });
};

export const bounceGroupPatterns = (sequencerEngine, group) => {
    return new Promise(async (resolve, reject) => {
        let lenInBars = 0;
        for (let patternNumber in group.tracks[0].patterns) {
            lenInBars +=  getPatternLength(sequencerEngine, parseInt(patternNumber), group.tracks) / 32;
        }
        lenInBars *= sequencerEngine.state.freeze.loops;
        resolve(await bouncePart({
            sequencerEngine,
            lenInBars,
            group: group.groupNumber.toString(),
            numPatterns: group.tracks[0].patterns.length,
            subSequence: sequencerEngine.currentSubSequence,
            bounceType: BOUNCE_PATTERNS,
            download: false,
            includeSends: true,
            tracks: group.tracks
        }));
        sequencerEngine.subSequences.forEach(
            sub => sub.mix.updateAllTypeRedux());
    });
};

export const bounce = (sequencerEngine, lenInBars=4, renderOption=MASTER, uploadToIpfs=false) => {
    return new Promise(async (resolve, reject) => {

        let title = sequencerEngine.state.sequencer.sequenceName;

        if (renderOption===MASTER) {
            await bouncePart({title, sequencerEngine, lenInBars, uploadToIpfs});
            resolve();
            return;
        }

        let totalToExport = sequencerEngine.sends.sends.length;
        for (let subSequence of sequencerEngine.subSequences) {
            totalToExport += subSequence.groups.groups.length;
        }

        let counter = 0;
        // otherwise we have to break apart by group
        for (let subSequence of sequencerEngine.subSequences) {
            let g = subSequence.groups;
            for (let group in g.groups) {
                sequencerEngine.dispatch(setCurrentExportInfo(
                    'Group ' + (parseInt(group) + 1) + ' (' + subSequence.name + ')',
                    counter,
                    totalToExport));
                await bouncePart({title, sequencerEngine, lenInBars, subSequence, group, uploadToIpfs});
                counter++;
            }
        }

        for (let send in sequencerEngine.sends.sends) {
            sequencerEngine.dispatch(setCurrentExportInfo(
                    'Send ' + SEND_NAMES[send],
                    counter,
                    totalToExport));
            await bouncePart({title, sequencerEngine, lenInBars, send: parseInt(send), uploadToIpfs});
            counter++;
        }
        resolve();
    });
};

export const bouncePart = ({
    title,
    sequencerEngine,
    lenInBars=4,
    subSequence,
    group,
    numPatterns,
    send,
    download=true,
    track,
    bounceType,
    includeSends=false,
    tracks,
    uploadToIpfs
}) => {
    return new Promise((resolve) => {
        let seconds = lenInBars * new Tone.Time('1n').toMilliseconds() / 1000;
        sequencerEngine.stop();
        let offlineEngine;
        Tone.Offline((transport) => {
            offlineEngine = new ScriptableSequencer(
                sequencerEngine.store, true, transport, sequencerEngine.aubio,
                true, // we dont need a metronome
                true // isOffline=true
            );
            offlineEngine.isOffline = true;
            sequencerEngine.store.dispatch(setOfflineEngine(offlineEngine));
            window.sequencerEngine = offlineEngine;
            sequencerEngine.store.dispatch(setIsPlayingMix(true));
            let scripting = new Scripting(offlineEngine);
            if (!bounceType) {
                offlineEngine.mix = sequencerEngine.mix;
            }
            offlineEngine.setScripting(scripting);
            offlineEngine.bpm = sequencerEngine.bpm;
            offlineEngine.bpmParameter.id = sequencerEngine.bpmParameter.id;
            offlineEngine.bpmParameter.min = sequencerEngine.bpmParameter.min;
            offlineEngine.bpmParameter.max = sequencerEngine.bpmParameter.max;
            offlineEngine.sends.copyFrom(sequencerEngine.sends);
            if (!bounceType) {
                // we only copy over the master channel strip if we are bouncing
                // the entire track
                offlineEngine.channelMixer.masterChannel.copyFrom(
                    sequencerEngine.channelMixer.masterChannel);
            }

            if (send !== undefined) {
                offlineEngine.sends.sends.filter(
                    (s, i) => i !== send)
                    .forEach(
                        send => {
                            send.outputVolume.disconnect();
                        });
            }

            if (group !== undefined || track !== undefined) {
                if (!includeSends) {
                    offlineEngine.sends.sends.forEach(send => send.outputVolume.disconnect());
                }
            }

            sequencerEngine.switchToSubSequence(sequencerEngine.subSequences[0].id);

            offlineEngine.subSequences[0].id = sequencerEngine.subSequences[0].id;
            for (let i in sequencerEngine.subSequences) {
                if (subSequence && subSequence.id !== sequencerEngine.subSequences[i].id) {
                    // skip this sub sequence
                    continue;
                }
                if (track && !sequencerEngine.subSequences[i].tracks.some(t => t.id === track.id)) {
                    // skip this sub sequence
                    continue;
                }

                if (i > 0) {
                    let sub = offlineEngine.newSubSequence();
                    sub.id = sequencerEngine.subSequences[i].id;
                    offlineEngine.switchToSubSequence(sub.id);
                    sequencerEngine.switchToSubSequence(sub.id);
                }

                offlineEngine.currentSubSequence.midiEffects.fromJson(
                    sequencerEngine.currentSubSequence.midiEffects.getJson());
                offlineEngine.lfos.forEach(
                    (lfoTrack, i) => lfoTrack.lfo.fromJson(sequencerEngine.lfos[i].lfo.getJson()));
                offlineEngine.envelopes.forEach(
                    (env, i) => env.fromJson(sequencerEngine.envelopes[i].getJson()));

                if (bounceType === BOUNCE_PATTERNS) {
                    setMixForBouncePattern(sequencerEngine, numPatterns, offlineEngine.currentSubSequence.mix, group ? tracks : [track]);
                } else if (bounceType === BOUNCE_INSTRUMENT) {
                    setMixForBounceInstrument(
                        sequencerEngine, numPatterns, offlineEngine.currentSubSequence.mix, group ? tracks : [track]);
                } else {
                    offlineEngine.currentSubSequence.mix = sequencerEngine.currentSubSequence.mix;
                }

                let {groups} = sequencerEngine;
                for (let j in groups.groups) {
                    let _group = groups.groups[j];
                    if (send !== undefined) {
                        // check if there are any tracks with non-silent sends,
                        // if there is not, then we skip this group
                        if (_group.tracks.every(
                            s =>
                                s.sendNodeVolumeParameters[send].get() <= SILENT &&
                                Object.values(s.sendNodeVolumeParameters[send]).every(
                                    v => v <= SILENT))) {
                        }
                    }

                    if (group !== undefined) {
                        if (j !== group) {
                            // check if there are any tracks that trigger envelopes,
                            // if there is not, then we skip this group
                            if (_group.tracks.every(s => s.connectedEnvelopes.length === 0)) {
                                continue;
                            } 
                        }
                    }

                    let offlineGroup =  offlineEngine.groups.newGroup();

                    offlineGroup.setVolume(_group.volume);
                    offlineGroup.setPanning(_group.panning);

                    // copy configuration for group
                    if (track !== undefined) {
                        // check if we can skip this group or not-- i.e. there are no tracks that trigger
                        // envelopes
                        if (_group !== track.group) {
                            if (_group.tracks.every(s => s.connectedEnvelopes.length === 0)) {
                                continue;
                            } 
                        }
                    }
                    let copiedGroupEffects = _group.effectsChain.effects.map(
                        e => e.getEffectCopy(offlineEngine, false));
                    offlineGroup.effectsChain.updateEffects(copiedGroupEffects);

                    // add the copied tracks to the group...
                    for (let i in _group.tracks) {
                        // ignoreId = false meaning we USE the id 
                        // copy the actual track
                        let _track = _group.tracks[i].getCopy(undefined, offlineEngine, false);
                        if (track) {
                            if (_group.tracks[i] !== track && _group.tracks[i].connectedEnvelopes.length == 0) {
                                // skip this track
                                continue;
                            }  
                        } 
                        offlineEngine.setupNewTrack(_track, undefined, undefined, false);
                        _track.setupEffects();
                        offlineGroup.addTrack(_track);
                        _track.trackNumber = _group.tracks[i].trackNumber;

                        if (track) {
                            if (_group.tracks[i] !== track) {
                                // mute this shit
                                _track.gainNode.disconnect();
                            }  
                        } 
                        if (bounceType === BOUNCE_INSTRUMENT) {
                            setPatternsForBounceInstrument(_track);
                        }
                    }

                    if (send !== undefined) {
                        offlineGroup.outputNode.disconnect();
                    }

                    if (group !== undefined) {
                        if (offlineEngine.currentSubSequence.id !== subSequence.id ||
                            group !== j) {
                            offlineGroup.outputNode.disconnect();
                        }
                    }


                }
                offlineEngine.tracks.sort((a,b) => a.trackNumber - b.trackNumber);
                offlineEngine.currentSubSequence.loadPadsJson(
                    sequencerEngine.currentSubSequence.getPadsJson());
            }

            // now search for any lost parameters and fix them then run
            for (let sub of offlineEngine.subSequences) {
                for (let track of sub.tracks) { 
                    track.loadLostParameters();
                }
            }

            offlineEngine.loadNewSequence();
            offlineEngine.play();
        }, seconds)
            .then(async (audioBuffer) => {
                let soundUploader = new SoundUploader(
                    2, (soundId, ipfsHash, loopNumber) => {
                        resolve(offlineEngine);
                        window.open("http://zequencer.io:8080/ipfs/" + ipfsHash, "_blank");
                    });
                
                let name = "Exported Song.wav";
                if (send !== undefined) {
                    name = "Send " + SEND_NAMES[send] + ".wav";
                }
                if (group !== undefined) {
                    name = "Group " + (parseInt(group) + 1) + " (" + subSequence.name + ").wav";
                }
                if (title) {
                    name = title + ' ' + name;
                }

                if (uploadToIpfs) {
                    await saveToIpfs(audioBuffer);
                } else if (download) {
                
                    let blob = [audioBuffer.getChannelData(0), audioBuffer.getChannelData(1)];
                    blob = await exportToWAV(blob);
                    saveData(blob, name);
                } 
                sequencerEngine.store.dispatch(setIsPlayingMix(false));

                offlineEngine.stop();
                window.sequencerEngine = sequencerEngine;
                sequencerEngine.store.dispatch(setOfflineEngine(undefined));
                offlineEngine.dispose();
                sequencerEngine.updateSteps();
                resolve(audioBuffer);
            });
    });
};

var saveData = (function () {
    var a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    return function (data, fileName) {
        var blob = new Blob([data], {type: "audio/wav"}),
            url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    };
}());

const saveToIpfs = async (buffer) => {
    let soundUploader = new SoundUploader(
        2, (soundId, ipfsHash, loopNumber) => []);
    
    let ipfsHash = await soundUploader.uploadBufferToIpfs(buffer);
    window.exportIpfsHash = ipfsHash;
    // now do something with that
    console.log("IPFS = ", ipfsHash);
};

export const getPatternLength = (sequencerEngine, patternNumber, tracks) => {
    let max = -10;
    for (let track of tracks) {
        for (let pattern of track.patterns) {
            let len = pattern.length();
            if (len > max) {
                max = len;
            }
        }
    }
    return max;
}

const setMixForBouncePattern = (sequencerEngine, numPatterns, mix, tracks) => {
    let loops = sequencerEngine.state.freeze.loops;
    let step = 0;
    for (let patternNumber=0; patternNumber < numPatterns; patternNumber++) {
        let len = getPatternLength(sequencerEngine, patternNumber, tracks) * loops;
        let event = new PatternSwitchEvent(patternNumber);
        event.step = step;
        mix.dispatch(insertEvent(event, len));
        step += len;
    }

};

const setPatternsForBounceInstrument = (track) => {
    let pattern = track.pattern;
    pattern.doubleTrackLength();
    track.patterns = [];
    let note = -12;
    for (let i=0; i < 24; i++) {
        let _pattern = pattern.getCopy();
        _pattern.clear();
        _pattern.addNote(0, 16, note + i*3);
        track.patterns.push(_pattern);
    }
    track.pattern = track.patterns[0];
};

const setMixForBounceInstrument = (sequencerEngine, numPatterns, mix, tracks) => {
    let loops = sequencerEngine.state.freeze.loops;
    let step = 0;
    let len = 64;
    let notes = 32;
    for (let patternNumber=0; patternNumber < notes; patternNumber++) {
        let event = new PatternSwitchEvent(patternNumber);
        event.step = step;
        mix.dispatch(insertEvent(event, len));
        step += len;
    }
};

